import React, { useState } from 'react';
import { PlayCircle, Plus, GripVertical, FileText, X, Video, Package, ExternalLink, Clock } from 'lucide-react';
import UploadArea from '../../../../../Components/UploadArea';
import { uploadLessonMp4, uploadLessonDocument, uploadLessonScorm } from '../../../../../services/UploadService';

interface SelectedModule {
  id: number;
  title: string;
  type: string;
  duration: string;
  contentType?: 'document' | 'scorm' | 'mp4' | 'url';
  file?: File | null;
  url?: string;
}

interface BlendedCourseProps {
  selectedModules: SelectedModule[];
  onOpenModulesModal: () => void;
  onRemoveModule: (moduleId: number) => void;
  onAddModule?: (module: SelectedModule) => void;
}

const BlendedCourse: React.FC<BlendedCourseProps> = ({
  selectedModules,
  onOpenModulesModal,
  onRemoveModule,
  onAddModule,
}) => {
  const [showCreateModuleForm, setShowCreateModuleForm] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleDuration, setNewModuleDuration] = useState('');
  const [newModuleContentType, setNewModuleContentType] = useState<'mp4' | 'document' | 'scorm' | 'url'>('mp4');
  const [newModuleFile, setNewModuleFile] = useState<File | null>(null);
  const [newModuleUrl, setNewModuleUrl] = useState('');

  const handleCreateModule = () => {
    if (!newModuleTitle.trim() || !newModuleDuration.trim()) {
      alert('Please fill in module title and duration');
      return;
    }

    if (newModuleContentType !== 'url' && !newModuleFile) {
      alert('Please upload a file or select URL content type');
      return;
    }

    if (newModuleContentType === 'url' && !newModuleUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    const newModule: SelectedModule = {
      id: Date.now(), // Temporary ID
      title: newModuleTitle,
      type: newModuleContentType === 'mp4' ? 'Video' : newModuleContentType === 'document' ? 'Document' : newModuleContentType === 'scorm' ? 'SCORM' : 'URL',
      duration: newModuleDuration,
      contentType: newModuleContentType,
      file: newModuleFile,
      url: newModuleUrl,
    };

    if (onAddModule) {
      onAddModule(newModule);
    }

    // Reset form
    setNewModuleTitle('');
    setNewModuleDuration('');
    setNewModuleContentType('mp4');
    setNewModuleFile(null);
    setNewModuleUrl('');
    setShowCreateModuleForm(false);
  };

  const handleFileUpload = async (file: File) => {
    setNewModuleFile(file);
    // Note: Actual upload will happen when course/lesson is saved with an ID
    // For now, just store the file object
  };

  const handleFileRemove = () => {
    setNewModuleFile(null);
  };
  return (
    <div className="trainer-coursemanagement-form-section">
      <div className="trainer-coursemanagement-section-header">
        <div className="trainer-coursemanagement-section-icon trainer-coursemanagement-orange">
          <PlayCircle size={24} color="white" />
        </div>
        <div>
          <h3>Course Modules ({selectedModules.length})</h3>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="trainer-coursemanagement-add-offering-btn"
            onClick={() => setShowCreateModuleForm(!showCreateModuleForm)}
          >
            <Plus size={16} /> Create Module
          </button>
          <button
            className="trainer-coursemanagement-add-offering-btn"
            onClick={onOpenModulesModal}
          >
            <Plus size={16} /> From Library
          </button>
        </div>
      </div>

      {/* Create Module Form - Same as WebBasedCourse */}
      {showCreateModuleForm && (
        <div className="trainer-coursemanagement-module-form" style={{
          marginBottom: '1.5rem',
          padding: '1.5rem',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Create New Module</h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Module Title *
              </label>
              <input
                type="text"
                value={newModuleTitle}
                onChange={(e) => setNewModuleTitle(e.target.value)}
                placeholder="e.g., Introduction to Leadership"
                className="trainer-coursemanagement-form-input"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Duration *
              </label>
              <input
                type="text"
                value={newModuleDuration}
                onChange={(e) => setNewModuleDuration(e.target.value)}
                placeholder="e.g., 45 min"
                className="trainer-coursemanagement-form-input"
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Content Type *
            </label>
            <select
              value={newModuleContentType}
              onChange={(e) => setNewModuleContentType(e.target.value as 'mp4' | 'document' | 'scorm' | 'url')}
              className="trainer-coursemanagement-form-select"
            >
              <option value="mp4">MP4 Video</option>
              <option value="document">Document (PDF, DOC)</option>
              <option value="scorm">SCORM Package</option>
              <option value="url">External URL</option>
            </select>
          </div>

          {newModuleContentType === 'url' ? (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Content URL *
              </label>
              <input
                type="url"
                value={newModuleUrl}
                onChange={(e) => setNewModuleUrl(e.target.value)}
                placeholder="https://example.com/content"
                className="trainer-coursemanagement-form-input"
              />
            </div>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                Upload {newModuleContentType === 'mp4' ? 'Video' : newModuleContentType === 'document' ? 'Document' : 'SCORM Package'} *
              </label>
              <UploadArea
                onUpload={handleFileUpload}
                onRemove={handleFileRemove}
                file={newModuleFile}
                icon={newModuleContentType === 'mp4' ? Video : newModuleContentType === 'document' ? FileText : Package}
                title={`Upload ${newModuleContentType} file`}
                description={
                  newModuleContentType === 'mp4'
                    ? 'MP4, MOV, AVI up to 500MB'
                    : newModuleContentType === 'document'
                    ? 'PDF, DOC, DOCX up to 50MB'
                    : 'ZIP file containing SCORM package'
                }
                accept={
                  newModuleContentType === 'mp4'
                    ? '.mp4,.mov,.avi'
                    : newModuleContentType === 'document'
                    ? '.pdf,.doc,.docx'
                    : '.zip'
                }
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              className="trainer-coursemanagement-btn-secondary"
              onClick={() => {
                setShowCreateModuleForm(false);
                setNewModuleTitle('');
                setNewModuleDuration('');
                setNewModuleContentType('mp4');
                setNewModuleFile(null);
                setNewModuleUrl('');
              }}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Cancel
            </button>
            <button
              className="trainer-coursemanagement-btn-primary"
              onClick={handleCreateModule}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Add Module
            </button>
          </div>
        </div>
      )}

      {selectedModules.length === 0 ? (
        <div className="trainer-coursemanagement-empty-state">
          <div className="trainer-coursemanagement-empty-icon">
            <PlayCircle size={48} />
          </div>
          <p className="trainer-coursemanagement-empty-subtitle">
            No modules added yet. Create a new module or add from library.
          </p>
        </div>
      ) : (
        <div className="trainer-coursemanagement-modules-list">
          {selectedModules.map((module, index) => (
            <div key={module.id} className="trainer-coursemanagement-module-item">
              <div className="trainer-coursemanagement-module-drag">
                <GripVertical size={20} />
              </div>
              <div className="trainer-coursemanagement-module-number">{index + 1}.</div>
              <div className="trainer-coursemanagement-module-icon">
                {module.type === 'Video' ? <Video size={24} color="#FF8C00" /> :
                 module.type === 'SCORM' ? <Package size={24} color="#FF8C00" /> :
                 module.type === 'URL' ? <ExternalLink size={24} color="#FF8C00" /> :
                 <FileText size={24} color="#FF8C00" />}
              </div>
              <div className="trainer-coursemanagement-module-info">
                <p className="trainer-coursemanagement-module-title">{module.title}</p>
                <p className="trainer-coursemanagement-module-meta">
                  {module.type} • {module.duration}
                  {module.file && ` • ${module.file.name}`}
                  {module.url && ` • ${module.url}`}
                </p>
              </div>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => onRemoveModule(module.id)}
              >
                <X size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlendedCourse;
